import React, { FC } from "react";

import { toast } from "react-toastify";

import {
  useApisManagersEmployeeConditionSurveysDestroy,
  useProvidersCurrentEmployee,
  useSearchManagersEmployeeConditionSurveys,
} from "~/hooks";

import {
  Form,
  Button,
  FormSubmitButton,
  GridHeaderCells,
  BooleanDropDownField,
} from "~/components/atoms";
import {
  EmployeeLayout,
  EmployeeLayoutMainContent,
} from "~/components/layouts";
import {
  LabelWithDropDownField,
  LabelWithTextField,
  GridRow,
  OpenedMenuInGrid,
  InfiniteScrollWithMoreButton,
} from "~/components/molecules";
import {
  MultiParentsWithMultiChildrenDropdownField,
  FilterWrapper,
  GridContainer,
} from "~/components/organisms";

import {
  EmployeeConditionSurveyAnswerShareOptions,
  EmployeeConditionSurveyType,
  OwnerRole,
} from "~/domains";

export const ManagersEmployeeConditionSurveysIndex: FC = () => {
  const { mutate: deleteRequest } =
    useApisManagersEmployeeConditionSurveysDestroy();
  const { currentEmployee } = useProvidersCurrentEmployee();
  const {
    employeeConditionSurveys,
    titleKeyword,
    isLoading,
    hasNextPage,
    allBusinessSectionsDistribution,
    selectableDropdownPublicStatusIds,
    selectedDropdownPublicStatusId,
    selectableDropdownDivisions,
    selectedDropdownDivisions,
    selectableDropdownSections,
    selectedDropdownSections,
    onDivisionChange,
    onSectionChange,
    findAndRemove,
    onChangeDropdownPublicStatusId,
    fetchNextPage,
    onChangeTitleKeyword,
    onConditionReset,
    onSearchSubmit,
    setAllBusinessSectionsDistribution,
  } = useSearchManagersEmployeeConditionSurveys();
  const breadCrumbItems = [
    {
      label: "コンディション",
      href: "/managers/employee_condition_surveys",
    },
    { label: "一覧", href: "/managers/employee_condition_surveys" },
  ];

  const handleDelete = (
    employeeConditionSurvey: EmployeeConditionSurveyType,
  ) => {
    const isConfirm = confirm("本当に削除しますか？");
    if (!isConfirm) return;

    deleteRequest(
      {
        id: employeeConditionSurvey.id,
      },
      {
        onSuccess: (data) => {
          toast(data.message);
          findAndRemove((item) => item.id === employeeConditionSurvey.id);
        },
      },
    );
  };

  const generateEmployeeConditionSurveyLists = (
    employeeConditionSurvey: EmployeeConditionSurveyType,
  ) => {
    return [
      employeeConditionSurvey.title,
      employeeConditionSurvey.allBusinessSectionsDistribution
        ? "すべての部署"
        : employeeConditionSurvey.businessDivisions
            .map((division) => division.name)
            .join(","),
      employeeConditionSurvey.allBusinessSectionsDistribution
        ? "すべての課"
        : employeeConditionSurvey.businessSections
            .map((section) => section.name)
            .join(","),
      employeeConditionSurvey.publicStatus.name,
    ];
  };

  return (
    <EmployeeLayout withRightSidebar={false} breadCrumbItems={breadCrumbItems}>
      <EmployeeLayoutMainContent withRightSidebar={false}>
        <FilterWrapper title="コンディション検索">
          <Form onSubmit={onSearchSubmit} className="space-y-6">
            <div className="grid miniTablet:grid-cols-2 gap-4">
              <MultiParentsWithMultiChildrenDropdownField
                allParents={selectableDropdownDivisions}
                parentsValue={selectedDropdownDivisions}
                parentLabel="部署"
                parentsOnChange={onDivisionChange}
                allChildren={selectableDropdownSections}
                childrenValue={selectedDropdownSections}
                childLabel="課"
                childrenOnChange={onSectionChange}
              />
              <BooleanDropDownField
                name="isAllBusinessSections"
                trueLabel="すべての課に配布する"
                falseLabel="個別の課に配布する"
                labelText="配布種別の選択"
                value={allBusinessSectionsDistribution}
                onChange={setAllBusinessSectionsDistribution}
              />
              <LabelWithDropDownField
                labelText="ステータス"
                name="publicStatus"
                options={selectableDropdownPublicStatusIds}
                value={selectedDropdownPublicStatusId}
                onChange={onChangeDropdownPublicStatusId}
              />
              <LabelWithTextField
                labelText="フリーワード"
                type="search"
                name="keyword"
                placeholder="フリーワード検索"
                value={titleKeyword}
                onChange={onChangeTitleKeyword}
              />
            </div>
            <div className="flex items-center justify-end space-x-4">
              <Button
                color="gray"
                outline
                className="w-full miniTablet:w-auto"
                text="リセット"
                onClick={onConditionReset}
              />
              <FormSubmitButton
                value="検索する"
                color="primary"
                className="w-full miniTablet:w-auto"
              />
            </div>
          </Form>
        </FilterWrapper>
        <InfiniteScrollWithMoreButton
          itemsLength={employeeConditionSurveys.length}
          nextFetchFunction={fetchNextPage}
          isLoading={isLoading}
          hasMore={hasNextPage}
          className="mt-9"
        >
          <GridContainer cols={4}>
            <GridHeaderCells texts={["タイトル", "部署", "課", "ステータス"]} />
            {employeeConditionSurveys.map((employeeConditionSurvey) => (
              <GridRow
                key={employeeConditionSurvey.id}
                lists={generateEmployeeConditionSurveyLists(
                  employeeConditionSurvey,
                )}
                openedMenu={
                  <OpenedMenuInGrid
                    cols={4}
                    buttons={[
                      {
                        text: "編集",
                        link: `/managers/employee_condition_surveys/${employeeConditionSurvey.id}/edit`,
                        isHidden:
                          currentEmployee?.employeeRole.type !== OwnerRole.type,
                      },
                      {
                        text: "削除",
                        onClick: () => handleDelete(employeeConditionSurvey),
                        isHidden:
                          currentEmployee?.employeeRole.type !== OwnerRole.type,
                      },
                      {
                        text: "投稿一覧",
                        link: `/managers/employee_condition_surveys/${employeeConditionSurvey.id}/progress`,
                        isHidden:
                          employeeConditionSurvey.answerShareOption.type ===
                            EmployeeConditionSurveyAnswerShareOptions.OWNER_ONLY
                              .type &&
                          currentEmployee?.employeeRole.type !== OwnerRole.type,
                      },
                    ]}
                  />
                }
              />
            ))}
          </GridContainer>
        </InfiniteScrollWithMoreButton>
      </EmployeeLayoutMainContent>
    </EmployeeLayout>
  );
};
